import { STORAGE_AREAS, type StorageBucket } from "./areas";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;

export interface ParsedObjectPath {
  readonly tenantId: string;
  readonly area: string;
  readonly rest: readonly string[];
}

/** Builds `{tenantId}/{area}/{...segments}` and refuses anything the storage policies would reject. */
export function buildObjectPath(
  bucket: StorageBucket,
  tenantId: string,
  area: string,
  ...segments: string[]
): string {
  if (!UUID.test(tenantId)) throw new TypeError("tenantId must be a uuid");
  if (!STORAGE_AREAS.some((a) => a.bucket === bucket && a.area === area))
    throw new TypeError(`unknown area ${area} for ${bucket}`);
  if (segments.length === 0)
    throw new TypeError("at least one path segment after the area is required");
  for (const s of segments) {
    if (!SEGMENT.test(s) || s === "." || s === "..")
      throw new TypeError(`invalid path segment ${JSON.stringify(s)}`);
  }
  return [tenantId.toLowerCase(), area, ...segments].join("/");
}

/** Parses an object name; returns null when it does not follow the contract. */
export function parseObjectPath(name: string): ParsedObjectPath | null {
  const parts = name.split("/");
  const [tenantId, area, ...rest] = parts;
  if (tenantId === undefined || area === undefined || rest.length === 0) return null;
  if (!UUID.test(tenantId) || area.length === 0 || rest.some((s) => s.length === 0)) return null;
  return { tenantId: tenantId.toLowerCase(), area, rest };
}

/** Public URL for an object in how-public. Private objects must use a signed URL from the server. */
export function publicObjectUrl(supabaseUrl: string, name: string): string {
  const parsed = parseObjectPath(name);
  if (parsed === null) throw new TypeError("name does not follow the tenant path contract");
  return `${supabaseUrl.replace(/\/$/, "")}/storage/v1/object/public/how-public/${name}`;
}
